import { Box, Typography } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetDevelopmentDetails } from 'src/lib/hooks/use-get-development-details';
import { useRoomAvailability } from 'src/lib/hooks/use-get-room-availability';
import { Spinner } from 'src/components/spinner';
import { appConfig } from 'src/app';
import { validateStaySelection } from 'src/utils/validators/validate-stay-selection';

import { FailurePage } from '../failure';

import { useRoomsSearchParams } from './hooks/use-rooms-search-params';
import { useStaySelection } from './hooks/use-stay-selection';
import { RoomsResults } from './rooms-results';
import { RoomsSearchPanel } from './rooms-search-panel';
import { styles } from './styles';

const DWS_DEVELOPMENT_SLUGS = [
    'first-street',
    'vita-student-at-strawberry-place-newcastle',
    'vita-student-leeds',
    'vita-student-glasgow',
    'vita-student-manchester-circle-square',
    'vita-student-edinburgh',
    'vita-student-birmingham',
];
const MINIMUM_STAY_DURATION = Number(appConfig.roomsAvailabilityMinimumStayDuration);
const MINIMUM_STAY_DURATION_DWS = Number(appConfig.roomsAvailabilityMinimumStayDurationDws);

export const RoomsContainer: FC = () => {
    const { t } = useTranslation();
    const {
        academicYear,
        developmentSlug,
        moveInDate,
        duration,
        hasPartialSearchParams,
        applySearch,
        clearSearch,
    } = useRoomsSearchParams();
    const {
        data: developmentDetails,
        isError: isDevelopmentError,
        isLoading: isLoadingDevelopment,
    } = useGetDevelopmentDetails(developmentSlug, academicYear);

    const urlErrorKey = hasPartialSearchParams
        ? 'rooms_page_error_invalid_move_in_date'
        : validateStaySelection({
              moveInDate,
              duration,
              moveOutDate: developmentDetails?.move_out_date,
              minimumStayDuration: DWS_DEVELOPMENT_SLUGS.includes(developmentSlug ?? '')
                  ? MINIMUM_STAY_DURATION_DWS
                  : MINIMUM_STAY_DURATION,
          });
    const urlError = urlErrorKey ? t(urlErrorKey) : null;

    const {
        selectedMoveInDate,
        selectedStayDuration,
        selectionError,
        hasSelectedDates,
        isSearchPending,
        setSelectionError,
        handleMoveInDateChange,
        handleDurationValidityChange,
        handleStayDurationChange,
    } = useStaySelection({
        initialMoveInDate: moveInDate,
        initialStayDuration: duration ? Number(duration) : null,
        appliedMoveInDate: moveInDate,
        appliedStayDuration: duration ? Number(duration) : null,
        urlError,
        applySearch,
        clearSearch,
    });

    const hasSearched = Boolean(moveInDate) && Boolean(duration) && !urlError;
    const hasInvalidRouteParams = !developmentSlug || !academicYear || hasPartialSearchParams;

    const {
        data: roomResults,
        isFetching: isLoadingRooms,
        isError: isRoomAvailabilityError,
    } = useRoomAvailability({
        developmentSlug,
        academicYear,
        moveInDate,
        duration: duration ? Number(duration) : null,
        enabled:
            Boolean(developmentDetails?.move_out_date) && hasSearched && !hasInvalidRouteParams,
    });

    if (isLoadingDevelopment) {
        return <Spinner />;
    }

    if (hasInvalidRouteParams || isDevelopmentError || !developmentDetails) {
        return <FailurePage />;
    }

    return (
        <Box>
            <Box css={styles.container}>
                <Box css={styles.wrapper}>
                    <Box css={styles.header}>
                        <Typography variant="h1" css={styles.title}>
                            {developmentDetails.name}
                        </Typography>
                        <Typography css={styles.subtitle}>{t('rooms_page_header')}</Typography>

                        <RoomsSearchPanel
                            minimumStayDuration={
                                DWS_DEVELOPMENT_SLUGS.includes(developmentSlug ?? '')
                                    ? MINIMUM_STAY_DURATION_DWS
                                    : MINIMUM_STAY_DURATION
                            }
                            moveOutDate={developmentDetails.move_out_date}
                            moveInDate={selectedMoveInDate}
                            stayDuration={selectedStayDuration}
                            onMoveInDateChange={handleMoveInDateChange}
                            onStayDurationChange={handleStayDurationChange}
                            onDurationValidityChange={handleDurationValidityChange}
                            onError={setSelectionError}
                        />
                    </Box>

                    <RoomsResults
                        error={selectionError}
                        isLoading={isLoadingRooms}
                        isSearchPending={isSearchPending}
                        isError={isRoomAvailabilityError}
                        results={roomResults}
                        hasSelectedDates={hasSelectedDates}
                        hasSearched={hasSearched}
                    />
                </Box>
            </Box>
        </Box>
    );
};
